import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Typography, Button, Space, Tag, message, Modal, Divider, Statistic, Radio, Alert, Spin, Input, Select, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  UserOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  GiftOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import CodeApporteurField from '../../components/parrainage/CodeApporteurField';
import AppLayout from '../../components/layout/AppLayout';
import ReglementEcole from './ReglementEcole';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useUserApi } from '../../api/user/userApp';
import { usePaiementApi, type TEtatReconduction, type CashplusInitiateResponse, type TEtatReglement, type TCodeEcoleInfo, type TEleveSelectionnable, type TResultatCodeApporteur, type TAvantageApporteur } from '../../api/paiement/paiement';
import { useBillingAdminApi, type BillingAdminLookupResult } from '../../api/billingAdmin/billingAdmin';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { useReferential } from '../../context/ReferentialContext';
import { CODIFICATION_TYPE_USERS, CODIFICATION_ROLES } from '../../constant/codification';
import { usePricingApi } from '../../api/paiement/pricing';
import { useAuth } from '../../context/AuthContext';
import './Paiement.css';

const { Search } = Input;

const { Text } = Typography;

type AccountType = 'parent' | 'organisation' | 'student';

// Marqueur d'un billing_userapp accordé sans paiement par un admin (voir tc_paiement).
const VENDOR_MANUAL_ADMIN = 'manual_admin';

/**
 * Cette ligne est-elle un compte que l'établissement ne peut PAS facturer ?
 *
 * L'école récupère sa liste par `GET /users_details`, qui rend tous les
 * comptes dont elle est le créateur — élèves ET tuteurs, car l'import scolaire
 * pose deux relations distinctes (`school_student` et `school_parent`) sans
 * que la requête ne filtre sur le type.
 *
 * Un établissement ne paie que les sièges de ses ÉLÈVES. Le serveur le sait
 * déjà : `FiltrerComptesDeLOrganisation` ne garde que les comptes présents
 * dans `school_students`, donc un panier contenant un tuteur part en 403.
 *
 * Mais le refus est global : cocher un parent faisait échouer TOUT le panier,
 * y compris les élèves légitimes, avec un message que l'école ne pouvait pas
 * relier à sa sélection. Mieux vaut l'empêcher ici, et le dire.
 *
 * Les comptes restent AFFICHÉS : l'école a besoin de voir que le compte tuteur
 * existe — c'est ce qu'elle doit transmettre aux familles.
 */
const compteNonFacturableParLEcole = (row: { type?: AccountType }, estEtablissement: boolean) =>
  estEtablissement && row.type !== 'student';

/**
 * Un paiement CashPlus est-il réellement en cours sur cette ligne ?
 *
 * Vrai seulement si un jeton existe ET n'est pas expiré : le client peut
 * encore aller le régler en agence, resélectionner la ligne ouvrirait un
 * second paiement pour la même chose.
 *
 * Faux pour un checkout CARTE abandonné : la facture reste en
 * `pending_payment` mais ne porte ni jeton ni expiration. Rien ne se passera
 * plus jamais dessus, la ligne doit donc redevenir sélectionnable — sans quoi
 * le compte est bloqué définitivement.
 */
const paiementCashplusEnCours = (row: { statut?: string; tokenExpiration?: string; pendingToken?: string }) => {
  if (row.statut !== 'pending_payment') return false;
  if (!row.pendingToken) return false; // pas un paiement CashPlus
  if (!row.tokenExpiration) return true; // jeton sans échéance connue : prudence
  return new Date(row.tokenExpiration) >= new Date();
};

type Account = {
  key: number; // userapp id
  name: string;
  type: AccountType;
  activeUntil?: string;       // localized label — set when statut=active
  activeUntilISO?: string;    // iso date used for local calc
  statut?: string;            // raw statut from backend
  vendor?: string;            // raw vendor from backend — 'manual_admin' = offert sans paiement
  pendingToken?: string;      // cashplus_token when statut=pending_payment
  pendingAmount?: number;     // total amount when statut=pending_payment
  tokenExpiration?: string;   // ISO — cashplus_token_expiration (24h window)
  // État de la RECONDUCTION, par enfant.
  //
  // Un parent qui a arrêté son abonnement reste premium jusqu'au terme payé.
  // S'il ne lit pas « pas de reconduction », il doute que son arrêt ait été
  // pris en compte et fait opposition auprès de sa banque — le pire cas.
  //
  // Absents pour un paiement CashPlus : il ne se reconduit pas, et proposer
  // d'arrêter un prélèvement qui n'existe pas serait faux.
  abonnementReference?: string;
  abonnementStatut?: string;
};

/** L'abonnement de cet enfant se reconduira-t-il ? */
const seReconduit = (row: { abonnementStatut?: string }) =>
  !!row.abonnementStatut && row.abonnementStatut !== 'cancelled' && row.abonnementStatut !== 'expired';

/** Le parent a arrêté : l'accès court jusqu'à son terme, la reprise est possible. */
const estArrete = (row: { abonnementStatut?: string }) => row.abonnementStatut === 'cancelled';

const toLocaleDate = (date: Date) =>
  date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const Paiement: React.FC = () => {
  const { t } = useTranslation();
  const userApi = useUserApi();
  const paiementApi = usePaiementApi();
  const billingAdminApi = useBillingAdminApi();
  const pricingApi = usePricingApi();
  const { getCodeTypeUser, getUserAppId, isRoleExist } = useTilmiAuth();
  const isAdminUser = useMemo(() => isRoleExist(CODIFICATION_ROLES.ADMIN_USERS), [isRoleExist]);
  const { getCodificationById, countries } = useReferential();
  const auth = useAuth();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // L'enfant dont l'arrêt ou la reprise est en cours. Un seul à la fois :
  // l'action porte sur une souscription précise, et un double clic ne doit pas
  // envoyer deux demandes.
  const [reconductionEnCours, setReconductionEnCours] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsFiltered, setAccountsFiltered] = useState<Account[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'expired'>('all');
  const [months, setMonths] = useState<number>(1);
  const [years, setYears] = useState<number>(1);
  const [mode, setMode] = useState<'months' | 'years'>('months');
  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  // Code de l'ecole. Il n'ouvre AUCUN droit : il applique le tarif negocie par
  // l'etablissement, que le parent paie. Le verifier ici n'est pas cosmetique —
  // sans cela l'ecran affiche le tarif public pendant que le serveur facture le
  // tarif negocie, et le parent est debite d'un autre montant que celui qu'il a lu.
  const [codeEcole, setCodeEcole] = useState<string>("");
  // Code de l'animateur qui a recommandé Tilmicode : distinct du code de
  // l'école, et jamais avec lui (le serveur refuse les deux ensemble).
  const [codeApporteur, setCodeApporteur] = useState<string>("");
  const [infoCodeEcole, setInfoCodeEcole] = useState<TCodeEcoleInfo | null>(null);
  // Élèves de l'établissement encore sans compte Tilmicode. Ils ne peuvent pas
  // être abonnés — le premium se lit sur le COMPTE, pas sur la fiche scolaire —
  // et n'apparaissent donc dans aucune liste de paiement. Sans ce signalement,
  // l'école ne peut pas savoir qu'il lui manque des comptes.
  const [elevesSansCompte, setElevesSansCompte] = useState<TEleveSelectionnable[]>([]);
  const [pricePerMonth, setPricePerMonth] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('');
  const [cashplusResult, setCashplusResult] = useState<CashplusInitiateResponse | null>(null);
  const [cashplusModalOpen, setCashplusModalOpen] = useState<boolean>(false);
  // Les comptes créés par le tunnel freemium n'ont pas de pays (l'étape 1 ne
  // demande que l'email) : sans lui, aucun tarif n'est applicable. On le
  // demande ici, au moment où il devient nécessaire.
  const [countryMissing, setCountryMissing] = useState<boolean>(false);
  const [countryDraft, setCountryDraft] = useState<string | undefined>();
  const [savingCountry, setSavingCountry] = useState<boolean>(false);

  // ── Section admin : accorder l'accès premium sans paiement ──────────────
  const [adminLoginInput, setAdminLoginInput] = useState<string>('');
  const [adminLookupLoading, setAdminLookupLoading] = useState<boolean>(false);
  const [adminLookupResult, setAdminLookupResult] = useState<BillingAdminLookupResult | null>(null);
  const [adminLookupError, setAdminLookupError] = useState<string | null>(null);
  const [adminExpirationDate, setAdminExpirationDate] = useState<Dayjs | null>(null);
  const [adminGranting, setAdminGranting] = useState<boolean>(false);

  const recomputeFiltered = (
    nextAccounts: Account[] = accounts,
    nextSearch: string = searchValue,
    nextStatus: 'all' | 'active' | 'pending' | 'expired' = statusFilter,
  ) => {
    const search = nextSearch.trim().toLowerCase();
    const now = new Date();
    const res = nextAccounts.filter((acc) => {
      const matchesSearch =
        search === '' || acc.name.toLowerCase().includes(search);

      const isExpiredToken = acc.statut === 'pending_payment' && !!acc.tokenExpiration && new Date(acc.tokenExpiration) < now;
      const isPending = acc.statut === 'pending_payment' && !isExpiredToken;
      const isActive = acc.statut === 'active' || !!acc.activeUntil;

      const matchesStatus =
        nextStatus === 'all' ? true :
          nextStatus === 'active' ? isActive :
            nextStatus === 'pending' ? isPending :
              nextStatus === 'expired' ? isExpiredToken :
                true;

      return matchesSearch && matchesStatus;
    });
    setAccountsFiltered(res);
  };

  const userType = useMemo(() => getCodeTypeUser(), [getCodeTypeUser]);
  /**
   * Un etablissement paie POUR SES ELEVES, au tarif negocie a son contrat.
   *
   * C'est le mode « l'ecole paie » : pas d'ecran dedie, c'est cette page. Elle
   * liste deja les comptes rattaches (pour une ecole, ses eleves), affiche leur
   * statut et permet la selection — il n'y manquait que le mode envoye au
   * serveur. Un ecran separe aurait duplique tout cela.
   */
  const estEtablissement = useMemo(
    () => userType === CODIFICATION_TYPE_USERS.BUSINESS,
    [userType]
  );
  const deuxCodes = !estEtablissement && codeEcole.trim() !== '' && codeApporteur.trim() !== '';

  /**
   * Etat de reglement remonte par « Votre reglement ».
   *
   * Sert a annoter la liste des comptes : le statut d'acces (« l'eleve est
   * premium ») et le statut de reglement (« l'ecole nous a payes ») sont DEUX
   * choses differentes. Un siege est actif des son ouverture, avant tout
   * paiement — sans cette colonne, l'ecole ne peut pas savoir ce qu'elle doit.
   */
  const [etatReglement, setEtatReglement] = useState<TEtatReglement | null>(null);

  /** userapp_id -> ou en est son reglement. */
  const reglementParEleve = useMemo(() => {
    const m = new Map<number, 'du' | 'en_attente' | 'regle'>();
    if (!etatReglement) return m;
    // Les regles d'abord : un eleve reinscrit peut figurer dans les deux, et
    // c'est la dette en cours qui doit l'emporter.
    for (const x of etatReglement.regles ?? []) m.set(x.student_id, 'regle');
    for (const x of etatReglement.a_regler ?? []) m.set(x.student_id, 'du');
    // Un reglement lance retire ses lignes des dus : ses eleves attendent
    // l'encaissement, ils ne sont ni dus ni regles.
    for (const id of etatReglement.en_cours?.eleves ?? []) m.set(id, 'en_attente');
    return m;
  }, [etatReglement]);
  const modePaiement = estEtablissement ? ('ecole' as const) : ('parent' as const);
  const currentUserId = useMemo(() => getUserAppId(), [getUserAppId]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    let accountsPromise: Promise<any>;
    if (userType === CODIFICATION_TYPE_USERS.ADULT || userType === CODIFICATION_TYPE_USERS.BUSINESS) {
      accountsPromise = userApi.getAllUsersAffected().then(({ data }) => data || []);
    } else {
      accountsPromise = userApi.getAllUserApps().then(({ data }) => data || []);
    }

    accountsPromise
      .then((data) => {
        if (!mounted) return;
        const mapped: Account[] = (data || []).map((u: any) => {
          const displayName =
            (u?.business_info?.name && String(u.business_info.name)) ||
            [u?.personal_info?.firstname, u?.personal_info?.lastname].filter(Boolean).join(' ') ||
            u?.login;

          let type: AccountType = 'parent';
          if (u?.business_info?.name) {
            type = 'organisation';
          } else {
            let code: string | undefined;
            if (u?.userType?.code) code = u.userType.code;
            else if (u?.usertype_id && typeof u.usertype_id === 'number') {
              const codif = getCodificationById(u.usertype_id);
              if (codif?.code) code = codif.code;
            }
            if (code === CODIFICATION_TYPE_USERS.ADULT) type = 'parent';
            else if (code === CODIFICATION_TYPE_USERS.BUSINESS) type = 'organisation';
            else if (code === CODIFICATION_TYPE_USERS.MINOR) type = 'student';
            else if (
              (userType === CODIFICATION_TYPE_USERS.ADULT || userType === CODIFICATION_TYPE_USERS.BUSINESS) &&
              u?.personal_info && !u?.business_info
            ) type = 'student';
          }

          return { key: u.id, name: displayName, type };
        });
        setAccounts(mapped);
        recomputeFiltered(mapped, searchValue, statusFilter);

        const ids = mapped.map((a) => Number(a.key)).filter((n) => Number.isFinite(n));
        if (ids.length > 0) {
          paiementApi
            .getStatuses(ids)
            .then(({ data }) => {
              const statuses: any[] = data?.statuses || [];
              const byId = new Map<number, any>();
              statuses.forEach((s) => {
                if (typeof s?.id_userapp === 'number') byId.set(s.id_userapp, s);
              });
              setAccounts((prev) => {
                const updated = prev.map((acc) => {
                  const st = byId.get(Number(acc.key));
                  if (!st) return acc;
                  try {
                    const statut: string = st?.statut || '';

                    if (statut === 'pending_payment') {
                      return {
                        ...acc,
                        statut,
                        pendingToken: st?.cashplus_token || '',
                        pendingAmount: st?.amount_totale ?? 0,
                        tokenExpiration: st?.cashplus_token_expiration ?? undefined,
                      };
                    }

                    const expISO: string | undefined = st?.date_expiration || st?.date_fin;
                    if (!expISO || typeof expISO !== 'string') return acc;
                    const df = new Date(expISO);
                    if (isNaN(df.getTime())) return acc;
                    return {
                      ...acc, statut, vendor: st?.vendor,
                      activeUntil: toLocaleDate(df), activeUntilISO: df.toISOString(),
                      // L'état de reconduction vient du serveur : lui seul
                      // connaît la souscription chez le prestataire.
                      abonnementReference: st?.abonnement_reference ?? undefined,
                      abonnementStatut: st?.abonnement_statut ?? undefined,
                    };
                  } catch {
                    return acc;
                  }
                });
                recomputeFiltered(updated, searchValue, statusFilter);
                return updated;
              });
            })
            .catch(() => { });
        }
      })
      .catch(() => {
        message.error(t('payment.messages.loadError', 'Erreur de chargement des comptes.'));
      })
      .finally(() => setLoading(false));

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [userType, currentUserId]);

  // Vérification du code école, débouncée le temps de la frappe.
  //
  // Sans elle, l'écran annonçait le tarif public pendant que le serveur
  // facturait le tarif négocié : le parent lisait un montant et était débité
  // d'un autre. Le serveur reste seul maître du prix — cet appel ne sert qu'à
  // afficher la même chose que lui.
  //
  // Un code invalide ne bloque rien : on retombe simplement sur le tarif
  // public, quitte à annoncer un montant trop élevé plutôt qu'un montant trop
  // bas.
  useEffect(() => {
    const code = codeEcole.trim();
    if (!code || estEtablissement) {
      setInfoCodeEcole(null);
      return;
    }
    let annule = false;
    const t = setTimeout(async () => {
      try {
        const info = await paiementApi.verifierCodeEcole(code);
        if (!annule) setInfoCodeEcole(info);
      } catch {
        if (!annule) setInfoCodeEcole(null);
      }
    }, 500);
    return () => {
      annule = true;
      clearTimeout(t);
    };
    // paiementApi n'est pas mémoïsé de façon stable : le mettre en dépendance
    // relancerait la vérification à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeEcole, estEtablissement]);

  // Élèves sans compte : information que l'écran ne savait pas donner, alors
  // que l'endpoint existait de bout en bout côté serveur sans être appelé.
  useEffect(() => {
    if (!estEtablissement) return;
    const orgID = getUserAppId();
    if (!orgID) return;
    let annule = false;
    (async () => {
      try {
        const eleves = await paiementApi.listerElevesSelectionnables(Number(orgID));
        if (!annule) setElevesSansCompte(eleves.filter((e) => e.sans_compte));
      } catch {
        // Information d'appoint : son absence ne doit pas empêcher de payer.
        if (!annule) setElevesSansCompte([]);
      }
    })();
    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estEtablissement]);

  // Load pricing once we know who the user is (keyed on user id so it fires
  // once after login and again if the account switches).
  useEffect(() => {
    const userId = (auth.user as any)?.user?.id ?? auth.user?.id;
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const items = await pricingApi.list();
        if (cancelled) return;
        const meta: any = (auth.user as any)?.user?.metadata || auth.user?.metadata || {};
        const rawCountry: unknown = meta?.pays ?? meta?.country ?? meta?.codepays;
        const country = typeof rawCountry === 'string' ? rawCountry.toUpperCase().trim() : '';
        const chosen = Array.isArray(items) && country
          ? items.find((p) => p.codepays?.toUpperCase() === country)
          : undefined;
        setPricePerMonth(chosen?.prix ?? 0);
        setCurrency(chosen?.devise ?? '');
        setCountryMissing(country === '');
      } catch (err) {
        console.error('[Paiement] pricing load error:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(auth.user as any)?.user?.id ?? auth.user?.id]);

  const saveCountry = async () => {
    if (!countryDraft || !currentUserId) return;
    setSavingCountry(true);
    try {
      await userApi.updateUserMetadata(Number(currentUserId), { pays: countryDraft });
      await auth.refreshMe();
      const items = await pricingApi.list();
      const chosen = items.find((p) => p.codepays?.toUpperCase() === countryDraft.toUpperCase());
      setPricePerMonth(chosen?.prix ?? 0);
      setCurrency(chosen?.devise ?? '');
      setCountryMissing(false);
      if (!chosen) {
        message.warning(t('payment.messages.noPricing', "Aucun tarif disponible pour votre pays. Contactez l'administrateur."));
      }
    } catch {
      message.error(t('payment.country.saveError', "Le pays n'a pas pu être enregistré."));
    } finally {
      setSavingCountry(false);
    }
  };

  const adminLookup = async () => {
    const login = adminLoginInput.trim();
    if (!login) return;
    setAdminLookupLoading(true);
    setAdminLookupError(null);
    setAdminLookupResult(null);
    try {
      const result = await billingAdminApi.lookup(login);
      setAdminLookupResult(result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAdminLookupError(t('payment.admin.notFound', 'Aucun compte trouvé pour ce login.'));
      } else {
        setAdminLookupError(t('payment.admin.lookupError', 'Erreur lors de la recherche.'));
      }
    } finally {
      setAdminLookupLoading(false);
    }
  };

  const adminGrantPremium = async () => {
    if (!adminLookupResult || !adminExpirationDate) return;
    setAdminGranting(true);
    try {
      await billingAdminApi.grantPremium(adminLookupResult.User.ID, adminExpirationDate.toISOString());
      message.success(
        t('payment.admin.grantSuccess', 'Accès premium accordé à {{login}} jusqu\'au {{date}}.', {
          login: adminLookupResult.User.Login,
          date: adminExpirationDate.format('DD/MM/YYYY'),
        })
      );
      setAdminLoginInput('');
      setAdminLookupResult(null);
      setAdminExpirationDate(null);
      // Rafraîchit le compte dans le tableau principal s'il y figure déjà.
      setAccounts((prev) => {
        const updated = prev.map((acc) =>
          acc.key === adminLookupResult.User.ID
            ? {
              ...acc,
              statut: 'active',
              vendor: VENDOR_MANUAL_ADMIN,
              activeUntil: toLocaleDate(adminExpirationDate.toDate()),
              activeUntilISO: adminExpirationDate.toISOString(),
            }
            : acc
        );
        recomputeFiltered(updated, searchValue, statusFilter);
        return updated;
      });
    } catch {
      message.error(t('payment.admin.grantError', "Échec de l'octroi de l'accès premium."));
    } finally {
      setAdminGranting(false);
    }
  };

  /**
   * Applique le nouvel état rendu par le serveur.
   *
   * Rien n'est affiché comme fait tant que le serveur ne l'a pas confirmé :
   * c'est la même règle que pour les paiements. Annoncer un arrêt qui n'a pas
   * eu lieu est précisément ce qui pousse un parent à faire opposition.
   */
  const appliquerEtatReconduction = (id: number, etat: TEtatReconduction) => {
    setAccounts((prev) => {
      const updated = prev.map((a) =>
        a.key === id
          ? { ...a, abonnementStatut: etat.reconduit ? 'active' : 'cancelled' }
          : a,
      );
      recomputeFiltered(updated, searchValue, statusFilter);
      return updated;
    });
  };

  /**
   * Confirmation avant l'arrêt, qui NOMME l'enfant et sa date de fin d'accès.
   *
   * Un parent doit savoir exactement ce qui va se passer : son enfant garde son
   * accès jusqu'à cette date, et aucun prélèvement n'aura lieu ensuite. Sans
   * cette précision, il croit couper l'accès immédiatement et renonce.
   */
  const confirmerArret = (row: Account) => {
    Modal.confirm({
      title: t('payment.renewal.confirmTitle', 'Arrêter le renouvellement ?'),
      content: t(
        'payment.renewal.confirmBody',
        '{{name}} garde son accès premium jusqu’au {{date}}. Aucun prélèvement n’aura lieu ensuite. Vous pourrez réactiver le renouvellement avant cette date, sans repayer.',
        { name: row.name, date: row.activeUntil ?? '' },
      ),
      okText: t('payment.renewal.confirmOk', 'Arrêter le renouvellement'),
      okButtonProps: { danger: true },
      cancelText: t('payment.renewal.confirmCancel', 'Annuler'),
      onOk: () => arreterReconduction(row),
    });
  };

  const arreterReconduction = async (row: Account) => {
    setReconductionEnCours(row.key);
    try {
      const etat = await paiementApi.arreterReconduction(row.key);
      appliquerEtatReconduction(row.key, etat);
      message.success(
        t('payment.renewal.stopped', 'Renouvellement arrêté. Accès jusqu’au {{date}}.', {
          date: etat.fin_acces ? new Date(etat.fin_acces).toLocaleDateString(i18n.language || 'fr') : '',
        }),
      );
    } catch (err: any) {
      // L'échec doit être dit : un parent qui croit son abonnement arrêté
      // alors que le prélèvement continuera fera opposition.
      message.error(
        t('payment.renewal.stopError', 'Le renouvellement n’a pas pu être arrêté. Réessayez ou contactez le support.'),
      );
    } finally {
      setReconductionEnCours(null);
    }
  };

  const reprendreReconduction = async (row: Account) => {
    setReconductionEnCours(row.key);
    try {
      const etat = await paiementApi.reprendreReconduction(row.key);
      appliquerEtatReconduction(row.key, etat);
      message.success(t('payment.renewal.resumed', 'Renouvellement réactivé.'));
    } catch (err: any) {
      message.error(
        t('payment.renewal.resumeError', 'Le renouvellement n’a pas pu être réactivé. La période est peut-être terminée.'),
      );
    } finally {
      setReconductionEnCours(null);
    }
  };

  const columns: ColumnsType<Account> = useMemo(
    () => [
      {
        title: t('payment.columns.account', 'Compte'),
        dataIndex: 'name',
        key: 'name',
        render: (text: string) => <Text strong>{text}</Text>,
      },
      {
        title: t('payment.columns.type', 'Type'),
        dataIndex: 'type',
        key: 'type',
        render: (value: AccountType) => {
          if (value === 'organisation') {
            return (
              <Space>
                <BankOutlined />
                <Tag color="purple">{t('payment.types.organisation', 'Organisation')}</Tag>
              </Space>
            );
          }
          if (value === 'student') {
            return (
              <Space>
                <TeamOutlined />
                <Tag color="green">{t('payment.types.student', 'Élève')}</Tag>
              </Space>
            );
          }
          return (
            <Space>
              <UserOutlined />
              <Tag color="geekblue">{t('payment.types.parent', 'Parent')}</Tag>
              {/* L'établissement voit le compte tuteur — c'est ce qu'il doit
                  transmettre aux familles — mais il ne le facture pas : il ne
                  paie que les sièges de ses élèves. Sans cette mention, la
                  ligne serait grisée sans raison apparente. */}
              {estEtablissement && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('payment.types.parentNotBillable', 'non facturable')}
                </Text>
              )}
            </Space>
          );
        },
      },
      {
        title: t('payment.columns.status', 'Statut'),
        key: 'status',
        render: (_: unknown, row: Account) => {
          if (row.statut === 'pending_payment') {
            const isExpired = row.tokenExpiration
              ? new Date(row.tokenExpiration) < new Date()
              : false;

            if (isExpired) {
              return (
                <Space>
                  <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Tag color="error">{t('payment.status.tokenExpired', 'Token expiré')}</Tag>
                  <Button
                    size="small"
                    type="primary"
                    danger
                    onClick={() => {
                      setSelectedRowKeys([row.key]);
                      setPaymentModalOpen(true);
                    }}
                  >
                    {t('payment.cashplus.regenerate', 'Nouveau token')}
                  </Button>
                </Space>
              );
            }

            // Checkout CARTE abandonné : `pending_payment` sans jeton
            // CashPlus. Rien n'aboutira plus sur cette facture, et la ligne
            // est de nouveau sélectionnable — il faut le DIRE, sinon
            // « En attente » laisse croire qu'un paiement se traite encore.
            if (!row.pendingToken) {
              return (
                <Space>
                  <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                  <Tag>{t('payment.status.abandoned', 'Paiement non finalisé')}</Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('payment.status.abandonedHint', 'Sélectionnez la ligne pour réessayer.')}
                  </Typography.Text>
                </Space>
              );
            }

            return (
              <Space>
                <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                <Tag color="warning">{t('payment.status.pending', 'En attente')}</Tag>
                {row.pendingToken && (
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      setCashplusResult({
                        id_billing: 0,
                        request_id: '',
                        cashplus_token: row.pendingToken!,
                        date_expiration: '',
                        total_amount: row.pendingAmount ?? 0,
                      });
                      setCashplusModalOpen(true);
                    }}
                  >
                    {t('payment.cashplus.viewToken', 'Voir le token')}
                  </Button>
                )}
              </Space>
            );
          }
          if (row.activeUntil && row.vendor === VENDOR_MANUAL_ADMIN) {
            return (
              <Space>
                <GiftOutlined style={{ color: '#722ed1' }} />
                <Tag color="purple">{t('payment.status.grantedByAdmin', 'Non payé (offert admin)')}</Tag>
              </Space>
            );
          }
          if (row.activeUntil) {
            // Un abonnement ARRÊTÉ reste actif jusqu'à son terme.
            //
            // Le dire est essentiel : un parent qui a arrêté et qui lit
            // seulement « Actif » croit que son arrêt n'a pas été pris en
            // compte, et fait opposition auprès de sa banque.
            if (estArrete(row)) {
              return (
                <Space>
                  <CheckCircleOutlined style={{ color: '#faad14' }} />
                  <Tag color="warning">
                    {t('payment.renewal.stopped', 'Pas de reconduction')}
                  </Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('payment.renewal.accessUntil', 'Accès jusqu’au {{date}}', {
                      date: row.activeUntil,
                    })}
                  </Typography.Text>
                </Space>
              );
            }
            return (
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Tag color="success">{t('payment.status.active', 'Actif')}</Tag>
                {seReconduit(row) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('payment.renewal.active', 'Se renouvelle automatiquement')}
                  </Typography.Text>
                )}
              </Space>
            );
          }
          return (
            <Space>
              <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
              <Tag>{t('payment.status.inactive', 'Inactif')}</Tag>
            </Space>
          );
        },
      },
      {
        title: t('payment.columns.activeUntil', 'Actif jusqu\'à'),
        dataIndex: 'activeUntil',
        key: 'activeUntil',
        render: (value?: string) => value || t('payment.status.none', '—'),
      },
      // Arrêter ou reprendre la reconduction, PAR ENFANT.
      //
      // N'apparaît QUE pour un abonnement par carte : CashPlus ne se reconduit
      // pas, et proposer d'arrêter un prélèvement qui n'existe pas serait faux.
      //
      // Réservée au parent : une école règle ses sièges d'un bloc et ne résilie
      // pas élève par élève.
      ...(estEtablissement
        ? []
        : [
            {
              title: t('payment.renewal.column', 'Renouvellement'),
              key: 'renewal',
              width: 190,
              render: (_: unknown, row: Account) => {
                if (!row.abonnementStatut) return null;
                if (estArrete(row)) {
                  return (
                    <Button
                      size="small"
                      loading={reconductionEnCours === row.key}
                      onClick={() => reprendreReconduction(row)}
                    >
                      {t('payment.renewal.resume', 'Réactiver')}
                    </Button>
                  );
                }
                if (!seReconduit(row)) return null;
                return (
                  <Button
                    size="small"
                    danger
                    loading={reconductionEnCours === row.key}
                    onClick={() => confirmerArret(row)}
                  >
                    {t('payment.renewal.stop', 'Arrêter le renouvellement')}
                  </Button>
                );
              },
            },
          ]),
      // Reservee a l'etablissement : « l'eleve est premium » et « nous sommes
      // payes » sont deux choses differentes. Un siege est actif des son
      // ouverture, avant tout paiement — sans cette colonne, l'ecole ne peut
      // pas savoir ce qu'elle doit encore.
      ...(estEtablissement
        ? [
            {
              title: t('payment.columns.settlement', 'Règlement'),
              key: 'settlement',
              width: 150,
              render: (_: unknown, row: Account) => {
                const etat = reglementParEleve.get(row.key);
                if (etat === 'regle') {
                  return <Tag color="success">{t('payment.settlement.paid', 'Payé')}</Tag>;
                }
                if (etat === 'en_attente') {
                  return <Tag color="warning">{t('payment.settlement.pending', 'En attente')}</Tag>;
                }
                if (etat === 'du') {
                  return <Tag color="error">{t('payment.settlement.due', 'Dû')}</Tag>;
                }
                // Aucun siege ecole pour cet eleve : ce compte ne passe pas par
                // le contrat (paye par son parent, ou hors contrat).
                return <Tag>{t('payment.settlement.none', 'Hors contrat')}</Tag>;
              },
            },
          ]
        : []),
    ],
    [t, estEtablissement, reglementParEleve]
  );

  // Le tarif negocie REMPLACE le tarif public, exactement comme le fait le
  // serveur (service.Tarifer). Les deux doivent afficher le meme montant.
  const tarifEcole = infoCodeEcole?.valide && !infoCodeEcole.ecole_paie ? infoCodeEcole : null;

  const openPaymentModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('payment.messages.noSelection', 'Sélectionnez au moins un compte.'));
      return;
    }
    // Le tarif public ne concerne que le parent. Une école est facturée au prix
    // négocié de son contrat, que le serveur résout à partir de son
    // organisation : exiger un tarif de pays ici l'empêcherait d'abonner ses
    // élèves. Même chose pour un parent porteur d'un code valide, dont le tarif
    // négocié remplace le tarif public.
    if (!estEtablissement && !tarifEcole && pricePerMonth <= 0) {
      message.error(t('payment.messages.noPricing', 'Aucun tarif disponible pour votre pays. Contactez l\'administrateur.'));
      return;
    }
    setSelectedVendor("");
    setPaymentModalOpen(true);
  };

  const addMonths = (d: Date, n: number) => {
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    // last day of target month
    const lastDayTarget = new Date(year, month + n + 1, 0).getDate();
    const newDay = Math.min(day, lastDayTarget);
    const res = new Date(d);
    res.setHours(d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
    res.setFullYear(year, month + n, newDay);
    return res;
  };

  const addYears = (d: Date, n: number) => {
    const res = new Date(d);
    res.setFullYear(d.getFullYear() + n);
    return res;
  };

  const buildDetails = () => {
    const now = new Date();
    const userIds = selectedRowKeys.map((k) => Number(k)).filter((n) => Number.isFinite(n));
    const amountPerMonth = pricePerMonth > 0 ? pricePerMonth : 0;
    const totalMonths = mode === 'years' ? Math.max(1, Math.floor(years)) * 12 : Math.max(1, Math.floor(months));
    const perUserAmount = amountPerMonth * totalMonths;
    const accountById = new Map(accounts.map((acc) => [Number(acc.key), acc]));
    const totalMonthsForDates = Math.max(1, totalMonths);
    const yearsToAdd = Math.floor(totalMonthsForDates / 12);
    const remainingMonths = totalMonthsForDates % 12;

    const details = userIds.map((id) => {
      const acc = accountById.get(id);
      const base = new Date(now);
      if (acc?.activeUntilISO) {
        const currentEnd = new Date(acc.activeUntilISO);
        if (!isNaN(currentEnd.getTime()) && currentEnd > base) base.setTime(currentEnd.getTime());
      }
      const afterYears = yearsToAdd > 0 ? addYears(base, yearsToAdd) : base;
      const end = remainingMonths > 0 ? addMonths(afterYears, remainingMonths) : afterYears;
      return {
        student_id: id,
        start_date: base.toISOString(),
        end_date: end.toISOString(),
        amount: perUserAmount,
      };
    });

    const totalAmount = details.reduce((sum, d) => sum + d.amount, 0);
    const generatedAt = details.reduce((min, d) => (d.start_date < min ? d.start_date : min), details[0]?.start_date || now.toISOString());
    const expirationDate = details.reduce((max, d) => (d.end_date > max ? d.end_date : max), details[0]?.end_date || now.toISOString());
    return { details, totalAmount, generatedAt, expirationDate };
  };

  const confirmPayment = async () => {
    if (!selectedVendor) {
      message.warning(t('payment.messages.vendorMissing', 'Veuillez choisir une méthode de paiement.'));
      return;
    }

    if (selectedVendor === 'cashplus') {
      await confirmCashplus();
      return;
    }

    if (selectedVendor === 'lemonsqueezy') {
      // Un abonnement par carte se souscrit PAR ENFANT.
      //
      // Un checkout Lemon Squeezy crée UNE souscription : payer trois enfants
      // en une fois produirait une souscription unique, qu'on ne pourrait
      // arrêter qu'en bloc. Le parent dont l'aîné arrête et dont le cadet
      // continue — la fin d'année scolaire — n'aurait aucune issue.
      //
      // Le serveur le refuse aussi, et c'est lui qui fait foi. Mais un 400 ne
      // dit rien au parent : on l'explique ici, avant l'appel, pour qu'il
      // sache quoi faire plutôt que de buter sur une erreur.
      //
      // CashPlus n'est pas concerné : il ne se reconduit pas, donc son panier
      // multiple reste le parcours normal.
      if (!estEtablissement && selectedRowKeys.length > 1) {
        message.warning(
          t(
            'payment.card.oneChildAtATime',
            'Le paiement par carte se fait un enfant à la fois, pour que vous puissiez arrêter l’abonnement de l’un sans toucher aux autres. Sélectionnez un seul enfant, ou choisissez CashPlus.',
          ),
        );
        return;
      }
      await confirmLemonsqueezy();
      return;
    }

    // Aucun autre moyen de paiement n'existe : les deux cartes proposees plus
    // haut sont les seules selectionnables. Cette branche servait autrefois a
    // ecrire une facture deja payee depuis le navigateur — statut et montant
    // fournis par le client. Elle a disparu avec la route qu'elle appelait.
    message.error(t('payment.messages.vendorUnsupported', 'Moyen de paiement non pris en charge.'));
  };

  // Le code animateur ne bloque jamais le paiement : s'il n'a pas pu servir,
  // on le dit au parent, le paiement se fait sans lui.
  const signalerCodeApporteur = (r?: TResultatCodeApporteur) => {
    if (!r || r.statut === 'applique') return;
    const messages: Record<TResultatCodeApporteur['statut'], string> = {
      applique: '',
      inactif: t('parrainage.paiement.inactif', "Le code animateur n'est plus actif : le paiement se fait sans lui."),
      inconnu: t('parrainage.paiement.inconnu', 'Code animateur inconnu : le paiement se fait sans lui.'),
      invalide: t('parrainage.paiement.inconnu', 'Code animateur inconnu : le paiement se fait sans lui.'),
      auto_attribution: t('parrainage.paiement.auto', "Un animateur ne peut pas utiliser son propre code pour ses enfants : le paiement se fait sans lui."),
      ignore: t('parrainage.paiement.ignore', "Le code animateur ne s'applique pas à ce paiement."),
      indisponible: t('parrainage.paiement.indisponible', "Le code animateur n'a pas pu être vérifié : le paiement se fait sans lui."),
    };
    message.warning(messages[r.statut]);
  };

  // L'avantage animateur retenu par le serveur, ligne par ligne : le montant
  // affiché ensuite (CashPlus, page de paiement par carte) en tient compte.
  const signalerAvantages = (avantages?: TAvantageApporteur[]) => {
    const vus = new Set<string>();
    (avantages ?? []).forEach((a) => {
      const cle = `${a.motif ?? ''}|${a.prix_public_mensuel}|${a.prix_applique_mensuel}|${a.devise}`;
      if (vus.has(cle)) return;
      vus.add(cle);
      if (a.motif === 'devise_differente') {
        message.warning(t('parrainage.paiement.avantageDevise', "L'avantage animateur ne s'applique pas dans cette devise : le tarif public s'applique."));
      } else if (a.motif === 'prix_nul') {
        message.warning(t('parrainage.paiement.avantagePrixNul', "L'avantage animateur ramènerait le prix à zéro : le tarif public s'applique."));
      } else {
        message.success(t('parrainage.paiement.avantage', 'Avantage animateur appliqué : {{public}} → {{applique}} {{devise}} par mois.', {
          public: a.prix_public_mensuel.toFixed(2),
          applique: a.prix_applique_mensuel.toFixed(2),
          devise: a.devise,
        }));
      }
    });
  };

  const confirmCashplus = async () => {
    try {
      setLoading(true);
      const { details, generatedAt, expirationDate } = buildDetails();
      const requestId = crypto.randomUUID();

      const res = await paiementApi.initiateCashplus({
        billing: {
          request_id: requestId,
          generated_at: generatedAt,
          expiration_date: expirationDate,
          details,
        },
        mode: modePaiement,
        // Applique le tarif negocie de l'ecole quand un code est saisi. Le
        // serveur recalcule le montant dans tous les cas : ce qui part d'ici
        // n'est qu'une demande, jamais un prix.
        //
        // En mode etablissement, aucun code n'est transmis : l'ecole est chez
        // elle, son contrat est retrouve par son organisation.
        code_ecole: estEtablissement ? undefined : codeEcole.trim() || undefined,
        code_apporteur: estEtablissement ? undefined : codeApporteur.trim() || undefined,
      });
      signalerCodeApporteur(res.data.code_apporteur);
      signalerAvantages(res.data.avantages_apporteur);

      // Mise à jour immédiate du statut sans refresh
      const token = res.data.cashplus_token;
      const amount = res.data.total_amount;
      const tokenExpiration = res.data.cashplus_token_expiration;
      const paidIds = new Set(details.map((d) => d.student_id));
      setAccounts((prev) => {
        const updated = prev.map((acc) =>
          paidIds.has(Number(acc.key))
            ? { ...acc, statut: 'pending_payment', pendingToken: token, pendingAmount: amount, tokenExpiration }
            : acc
        );
        recomputeFiltered(updated, searchValue, statusFilter);
        return updated;
      });

      setCashplusResult(res.data);
      setPaymentModalOpen(false);
      setCashplusModalOpen(true);
    } catch {
      message.error(t('payment.cashplus.initiateError', 'Impossible de générer le token CashPlus. Réessayez.'));
    } finally {
      setLoading(false);
    }
  };

  // Abonnement carte : contrairement a CashPlus qui affiche un code, on quitte
  // l'application pour la page de paiement hebergee. Le retour se fait sur
  // LEMONSQUEEZY_REDIRECT_URL, et ce sont les webhooks qui activent les comptes.
  const confirmLemonsqueezy = async () => {
    try {
      setLoading(true);
      const { details, generatedAt, expirationDate } = buildDetails();

      const res = await paiementApi.initiateLemonsqueezy({
        billing: {
          request_id: crypto.randomUUID(),
          generated_at: generatedAt,
          expiration_date: expirationDate,
          // amount volontairement omis : le serveur le recalcule.
          details: details.map(({ student_id, start_date, end_date }) => ({
            student_id,
            start_date,
            end_date,
          })),
        },
        mode: modePaiement,
        code_ecole: estEtablissement ? undefined : codeEcole.trim() || undefined,
        code_apporteur: estEtablissement ? undefined : codeApporteur.trim() || undefined,
      });
      signalerCodeApporteur(res.data.code_apporteur);
      signalerAvantages(res.data.avantages_apporteur);

      // Redirection plutot qu'un nouvel onglet : un popup bloque laisserait
      // l'utilisateur devant un ecran qui ne se passe rien.
      window.location.assign(res.data.checkout_url);
    } catch (err: any) {
      // 422 = tarif du pays inexploitable (taux de conversion manquant) : le
      // message du serveur nomme ce qui manque, il est plus utile que le notre.
      const detail = err?.response?.data?.error;
      message.error(
        detail ||
          t('payment.lemonsqueezy.initiateError', "Impossible d'ouvrir la page de paiement. Réessayez.")
      );
      setLoading(false);
    }
  };

  const selectedCount = selectedRowKeys.length;
  const activeCount = useMemo(() => accounts.filter((acc) => acc.activeUntil).length, [accounts]);
  const totalCount = accounts.length;
  const prixMensuelApplique = tarifEcole ? tarifEcole.prix ?? 0 : pricePerMonth;
  const deviseAppliquee = tarifEcole ? tarifEcole.devise ?? currency : currency;

  const perUserAmount = useMemo(() => {
    const totalMonths = mode === 'years' ? Math.max(1, years) * 12 : Math.max(1, months);
    return prixMensuelApplique > 0 ? prixMensuelApplique * totalMonths : 0;
  }, [prixMensuelApplique, months, years, mode]);
  const totalAmount = useMemo(() => perUserAmount * selectedCount, [perUserAmount, selectedCount]);

  return (
    <AppLayout title={t('payment.title', 'Paiement')} loading={loading}>
      <div className="payment-page-container">
        {/* Header Section */}
        <Card className="payment-header-card">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Typography.Title level={3} className="payment-page-title">
                {t('payment.heading', 'Activer des comptes')}
              </Typography.Title>
              <Typography.Text className="payment-page-description">
                {t('payment.description', "Sélectionnez les comptes à activer, choisissez le nombre de mois, puis lancez le paiement.")}
              </Typography.Text>
            </div>
            <Divider style={{ margin: '16px 0' }} />
            <Space size="large" wrap>
              <Statistic
                title={t('payment.stats.total', 'Total comptes')}
                value={totalCount}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#2992bf', fontWeight: 700 }}
              />
              <Statistic
                title={t('payment.stats.active', 'Comptes actifs')}
                value={activeCount}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a', fontWeight: 700 }}
              />
              {selectedCount > 0 && (
                <Statistic
                  title={t('payment.selectedCount', 'Compte(s) sélectionné(s)')}
                  value={selectedCount}
                  prefix={<CreditCardOutlined />}
                  valueStyle={{ color: '#8bbe1e', fontWeight: 700 }}
                />
              )}
              {selectedCount > 0 && (
                <Statistic
                  title={t('payment.stats.totalAmount', 'Total à payer')}
                  value={totalAmount}
                  precision={2}
                  suffix={deviseAppliquee}
                  valueStyle={{ color: '#8bbe1e', fontWeight: 700 }}
                />
              )}
            </Space>
          </Space>
        </Card>

        {/* Ce que l'établissement nous doit. Visible seulement pour lui : un
            parent n'a pas de sièges à régler. Place juste sous l'en-tête, avant
            la liste des comptes — c'est l'action qui l'amène ici. */}
        {estEtablissement && elevesSansCompte.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('payment.ecole.sansCompteTitle', '{{count}} élève(s) sans compte Tilmicode', {
              count: elevesSansCompte.length,
            })}
            description={
              <>
                <div style={{ marginBottom: 8 }}>
                  {t(
                    'payment.ecole.sansCompteHint',
                    "Ces élèves ne peuvent pas être abonnés tant qu'ils n'ont pas de compte. Relancez l'import depuis TilmiSchool pour le leur créer."
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {elevesSansCompte
                    .slice(0, 10)
                    .map((e) => `${e.prenom} ${e.nom}`)
                    .join(', ')}
                  {elevesSansCompte.length > 10 ? '…' : ''}
                </Text>
              </>
            }
          />
        )}
        {estEtablissement && <ReglementEcole onEtat={setEtatReglement} />}

        {/* Section admin : accorder l'accès premium sans paiement (réservé ADMIN_USERS) */}
        {isAdminUser && (
          <Card className="payment-header-card" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                <GiftOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                {t('payment.admin.title', "Accorder l'accès premium sans paiement")}
              </Typography.Title>
              <Space wrap>
                <Input
                  placeholder={t('payment.admin.loginPlaceholder', 'Login du compte')}
                  value={adminLoginInput}
                  onChange={(e) => setAdminLoginInput(e.target.value)}
                  onPressEnter={adminLookup}
                  style={{ width: 220 }}
                />
                <Button icon={<SearchOutlined />} onClick={adminLookup} loading={adminLookupLoading} disabled={!adminLoginInput.trim()}>
                  {t('payment.admin.search', 'Chercher')}
                </Button>
              </Space>

              {adminLookupError && (
                <Alert type="error" showIcon message={adminLookupError} />
              )}

              {adminLookupResult && (
                <Alert
                  type="info"
                  showIcon
                  message={`${adminLookupResult.User.Nom} (${adminLookupResult.User.Login}, id=${adminLookupResult.User.ID})`}
                  description={
                    adminLookupResult.Current
                      ? t(
                        'payment.admin.currentStatus',
                        'Ce compte a déjà un abonnement "{{statut}}" (prestataire: {{vendor}}) jusqu\'au {{date}}.',
                        {
                          statut: adminLookupResult.Current.Statut,
                          vendor: adminLookupResult.Current.Vendor || '—',
                          date: new Date(adminLookupResult.Current.DateFin).toLocaleDateString('fr-FR'),
                        }
                      )
                      : t('payment.admin.noCurrentStatus', 'Aucun abonnement actif pour ce compte.')
                  }
                />
              )}

              {adminLookupResult && (
                <Space wrap>
                  <DatePicker
                    placeholder={t('payment.admin.expirationPlaceholder', "Date d'expiration")}
                    value={adminExpirationDate}
                    onChange={setAdminExpirationDate}
                    disabledDate={(d: Dayjs) => d.isBefore(dayjs().startOf('day'))}
                    format="DD/MM/YYYY"
                  />
                  <Button
                    type="primary"
                    icon={<GiftOutlined />}
                    onClick={adminGrantPremium}
                    loading={adminGranting}
                    disabled={!adminExpirationDate}
                  >
                    {t('payment.admin.grant', "Accorder l'accès premium")}
                  </Button>
                </Space>
              )}
            </Space>
          </Card>
        )}

        {/* Pays manquant : bloque le calcul du tarif tant qu'il n'est pas renseigné */}
        {countryMissing && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('payment.country.title', 'Indiquez votre pays')}
            description={
              <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 8 }}>
                <Text type="secondary">
                  {t('payment.country.description', 'Nos tarifs dépendent du pays. Renseignez-le pour activer vos comptes.')}
                </Text>
                <Space wrap>
                  <Select
                    showSearch
                    style={{ minWidth: 240 }}
                    value={countryDraft}
                    onChange={setCountryDraft}
                    optionFilterProp="label"
                    placeholder={t('payment.country.placeholder', 'Sélectionnez votre pays')}
                    options={countries.map((c) => ({ value: c.code, label: c.name }))}
                  />
                  <Button type="primary" loading={savingCountry} disabled={!countryDraft} onClick={saveCountry}>
                    {t('payment.country.save', 'Enregistrer')}
                  </Button>
                </Space>
              </Space>
            }
          />
        )}

        {/* Table Card */}
        <Card className="payment-table-card">
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {/* Barre de recherche + filtre statut */}
            <div className="payment-filters-row">
              <Search
                placeholder={t('payment.filters.searchPlaceholder', 'Rechercher par nom...')}
                allowClear
                value={searchValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchValue(v);
                  recomputeFiltered(accounts, v, statusFilter);
                }}
                onSearch={(v) => {
                  setSearchValue(v);
                  recomputeFiltered(accounts, v, statusFilter);
                }}
                style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
              />
              <Select
                value={statusFilter}
                style={{ minWidth: 200 }}
                onChange={(v: 'all' | 'active' | 'pending' | 'expired') => {
                  setStatusFilter(v);
                  recomputeFiltered(accounts, searchValue, v);
                }}
                options={[
                  { value: 'all', label: t('payment.filters.all', 'Tous les statuts') },
                  { value: 'active', label: t('payment.filters.active', 'Actif') },
                  { value: 'pending', label: t('payment.filters.pending', 'En attente') },
                  { value: 'expired', label: t('payment.filters.expired', 'Token expiré') },
                ]}
              />
            </div>
            {selectedCount > 0 && (
              <div className="payment-selection-info">
                <Space>
                  <InfoCircleOutlined style={{ color: '#2992bf', fontSize: 18 }} />
                  <Text strong style={{ color: '#2992bf' }}>
                    {t('payment.selectedCount', '{{count}} compte(s) sélectionné(s).', { count: selectedCount })}
                  </Text>
                </Space>
              </div>
            )}
            <Table
              className="payment-accounts-table"
              // Pas de selection pour un etablissement : elle ne menerait a
              // aucune action, la liste devient une simple consultation.
              // La sélection vaut pour les deux acteurs : un parent choisit ses
              // enfants, une école ses élèves. Elle était désactivée pour
              // l'école du temps où un siège s'ouvrait d'office ; c'est
              // désormais son seul moyen d'abonner.
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                // Un compte déjà couvert — actif, ou dont le paiement est en
                // attente — n'est pas sélectionnable : le proposer ferait payer
                // deux fois le même élève. Il reste visible avec son statut :
                // on doit voir toute la liste, y compris ce qui est déjà réglé.
                getCheckboxProps: (row: Account) => ({
                  // Un compte déjà actif n'a rien à payer.
                  //
                  // Un `pending_payment` n'est bloqué que s'il correspond à un
                  // paiement RÉELLEMENT en cours — c'est-à-dire un jeton
                  // CashPlus encore valide, que le client peut aller régler en
                  // agence. Le rebloquer évite la double soumission.
                  //
                  // Mais un checkout CARTE abandonné laisse lui aussi la
                  // facture en `pending_payment`, SANS jeton ni expiration :
                  // la ligne restait alors ingérable à vie. L'utilisateur
                  // ouvrait la page du prestataire, ne payait pas, revenait —
                  // et ne pouvait plus ni réessayer, ni payer autrement.
                  disabled:
                    row.statut === 'active' ||
                    Boolean(row.activeUntil) ||
                    paiementCashplusEnCours(row) ||
                    compteNonFacturableParLEcole(row, estEtablissement),
                }),
              }}
              columns={columns}
              dataSource={accountsFiltered}
              rowKey={(row) => row.key}
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} ${t('payment.table.total', 'comptes')}` }}
            />
            {/* L'etablissement abonne ses eleves d'ici, en les selectionnant.
                Ce bouton etait masque du temps ou un siege s'ouvrait d'office
                et se refacturait ensuite : « Votre reglement » suffisait alors
                a solder ce qui etait deja ouvert. Depuis qu'un acces ne
                s'ouvre plus sans paiement, c'est le seul chemin par lequel une
                ecole peut abonner ses eleves.

                Les deux sections ne se confondent pas : ici on PAIE pour
                ouvrir un acces, la-bas on solde une dette ancienne. Les lignes
                deja couvertes ne sont pas selectionnables, elles ne peuvent
                donc pas etre refacturees. */}
            <div className="payment-actions-section">
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                onClick={openPaymentModal}
                disabled={selectedRowKeys.length === 0}
                className="payment-activate-button"
              >
                {estEtablissement
                  ? t('payment.actions.subscribeStudents', 'Abonner les élèves sélectionnés')
                  : t('payment.actions.activate', 'Procéder au paiement')}
                {selectedCount > 0 && ` (${selectedCount})`}
              </Button>
            </div>
          </Space>
        </Card>
      </div>

      {/* ── Modal token CashPlus ── */}
      <Modal
        title={null}
        open={cashplusModalOpen}
        footer={[
          <Button
            key="close"
            type="primary"
            size="large"
            block
            onClick={() => { setCashplusModalOpen(false); setSelectedRowKeys([]); }}
            style={{ borderRadius: 10, background: 'linear-gradient(135deg, #2992bf, #8bbe1e)', border: 'none', fontWeight: 600 }}
          >
            {t('payment.cashplus.close', 'Fermer')}
          </Button>,
        ]}
        onCancel={() => { setCashplusModalOpen(false); setSelectedRowKeys([]); }}
        width={460}
        styles={{ body: { padding: 0 } }}
        className="cashplus-token-modal"
      >
        {cashplusResult ? (
          <div className="cashplus-modal-inner">
            {/* Header avec logo */}
            <div className="cashplus-modal-header">
              <img src="/images/Logo_CASHPLUS.png" alt="CashPlus" className="cashplus-modal-logo" />
              <Typography.Text className="cashplus-modal-header-subtitle">
                {t('payment.cashplus.instructions', 'Présentez ce code dans n\'importe quelle agence CashPlus pour finaliser votre paiement.')}
              </Typography.Text>
            </div>

            {/* Token */}
            <div className="cashplus-token-section">
              <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                {t('payment.cashplus.tokenLabel', 'Code de paiement')}
              </Typography.Text>
              <div className="cashplus-token-display">
                <span className="cashplus-token-value">{cashplusResult.cashplus_token}</span>
                <Button
                  icon={<CopyOutlined />}
                  type="text"
                  className="cashplus-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(cashplusResult.cashplus_token);
                    message.success(t('payment.cashplus.copied', 'Code copié !'));
                  }}
                />
              </div>
            </div>

            {/* Détails */}
            <div className="cashplus-modal-details">
              <div className="cashplus-detail-row">
                <span className="cashplus-detail-label">{t('payment.cashplus.amount', 'Montant à payer')}</span>
                <span className="cashplus-detail-value">{cashplusResult.total_amount.toFixed(2)} <span style={{ fontSize: 13, fontWeight: 500 }}>{currency}</span></span>
              </div>
              {cashplusResult.cashplus_token_expiration && (
                <div className="cashplus-detail-row">
                  <span className="cashplus-detail-label">{t('payment.cashplus.expires', 'Valable jusqu\'au')}</span>
                  <span className="cashplus-detail-expiry">{new Date(cashplusResult.cashplus_token_expiration).toLocaleString('fr-FR')}</span>
                </div>
              )}
            </div>

            <div className="cashplus-modal-footer-note">
              {t('payment.cashplus.activationNote', 'Votre compte sera activé automatiquement dès réception du paiement.')}
            </div>
          </div>
        ) : (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
        )}
      </Modal>

      <Modal
        className="payment-modal"
        title={t('payment.modal.title', "Choisir le nombre de mois")}
        open={paymentModalOpen}
        onOk={confirmPayment}
        okText={t('payment.modal.ok', 'Valider le paiement')}
        onCancel={() => setPaymentModalOpen(false)}
        cancelText={t('payment.modal.cancel', 'Annuler')}
        confirmLoading={loading}
        okButtonProps={{ disabled: !selectedVendor || deuxCodes }}
        width={520}
        styles={{
          content: {
            borderRadius: '20px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(247, 250, 252, 0.98))',
            border: '1px solid rgba(224, 231, 241, 0.8)',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.15), 0 8px 32px rgba(15, 23, 42, 0.1)',
            padding: 0,
          },
          header: {
            background: 'linear-gradient(135deg, rgba(41, 146, 191, 0.08), rgba(139, 190, 30, 0.08))',
            borderBottom: '1px solid rgba(224, 231, 241, 0.6)',
            padding: '24px 32px 20px',
            borderRadius: '20px 20px 0 0',
          },
          body: {
            padding: '32px',
          },
          footer: {
            padding: '20px 32px 24px',
            borderTop: '1px solid rgba(224, 231, 241, 0.6)',
            background: 'rgba(247, 250, 252, 0.5)',
          },
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Radio.Group
            className="period-selection-group"
            onChange={(e) => setMode(e.target.value)}
            value={mode}
            optionType="button"
            style={{ display: 'flex', width: '100%' }}
          >
            <Radio.Button value="months">{t('payment.period.months', 'Paiement mensuel')}</Radio.Button>
            <Radio.Button value="years">{t('payment.period.years', 'Paiement annuel')}</Radio.Button>
          </Radio.Group>
          {mode === 'months' ? (
            <Space align="center" style={{ width: '100%', justifyContent: 'center' }}>
              <Button onClick={() => setMonths((m) => Math.max(1, m - 1))} shape="circle" size="large">−</Button>
              <Typography.Title level={3} style={{ margin: '0 16px' }}>
                {months} {t('payment.months', 'mois')}
              </Typography.Title>
              <Button onClick={() => setMonths((m) => Math.min(120, m + 1))} shape="circle" size="large">+</Button>
            </Space>
          ) : (
            <Space align="center" style={{ width: '100%', justifyContent: 'center' }}>
              <Button onClick={() => setYears((y) => Math.max(1, y - 1))} shape="circle" size="large">−</Button>
              <Typography.Title level={3} style={{ margin: '0 16px' }}>
                {years} {t('payment.years', years > 1 ? 'ans' : 'an')}
              </Typography.Title>
              <Button onClick={() => setYears((y) => Math.min(10, y + 1))} shape="circle" size="large">+</Button>
            </Space>
          )}
          <Divider style={{ margin: '8px 0' }} />
          <div>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Récapitulatif montants */}
              <div className="payment-summary-box">
                {/* Le prix négocié d'un établissement vit dans son contrat et
                    n'est résolu que par le serveur : l'annoncer ici obligerait
                    à le recopier côté navigateur, avec le risque de diverger.
                    Mieux vaut dire honnêtement que le montant sera confirmé
                    qu'afficher un 0,00 qui serait faux. */}
                {estEtablissement ? (
                  <div className="payment-summary-row payment-summary-row--total">
                    <span className="payment-summary-label">
                      {selectedCount} {t('payment.table.total', 'compte(s)')}
                    </span>
                    <span className="payment-summary-value">
                      {t('payment.ecole.amountOnNextStep', 'Montant confirmé à l\'étape suivante')}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="payment-summary-row">
                      <span className="payment-summary-label">{t('payment.perUserLabel', 'Montant par compte')}</span>
                      <span className="payment-summary-value">{perUserAmount.toFixed(2)} <span className="payment-summary-currency">{deviseAppliquee}</span></span>
                    </div>
                    <div className="payment-summary-row payment-summary-row--total">
                      <span className="payment-summary-label">{t('payment.totalAmountLabel', 'Total')} · {selectedCount} {t('payment.table.total', 'compte(s)')}</span>
                      <span className="payment-summary-value-total">{totalAmount.toFixed(2)} <span className="payment-summary-currency">{deviseAppliquee}</span></span>
                    </div>
                  </>
                )}
              </div>
              <Divider style={{ margin: '4px 0 8px' }} />
              {/* Sélecteur méthode de paiement */}
              <Text strong style={{ fontSize: 14, color: '#0f172a' }}>{t('payment.vendor.label', 'Méthode de paiement')}</Text>
              <div className="payment-vendor-grid">
                <div
                  className={`payment-vendor-card${selectedVendor === 'cashplus' ? ' selected' : ''}`}
                  onClick={() => setSelectedVendor('cashplus')}
                  role="radio"
                  aria-checked={selectedVendor === 'cashplus'}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedVendor('cashplus')}
                >
                  <img
                    src="/images/Logo_CASHPLUS.png"
                    alt="CashPlus"
                    className="payment-vendor-logo"
                  />
                  {selectedVendor === 'cashplus' && (
                    <div className="payment-vendor-check">
                      <CheckCircleOutlined />
                    </div>
                  )}
                </div>
                <div
                  className={`payment-vendor-card${selectedVendor === 'lemonsqueezy' ? ' selected' : ''}`}
                  onClick={() => setSelectedVendor('lemonsqueezy')}
                  role="radio"
                  aria-checked={selectedVendor === 'lemonsqueezy'}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedVendor('lemonsqueezy')}
                >
                  <span className="payment-vendor-label">
                    <CreditCardOutlined />
                    {t('payment.vendor.card', 'Carte bancaire')}
                  </span>
                  {selectedVendor === 'lemonsqueezy' && (
                    <div className="payment-vendor-check">
                      <CheckCircleOutlined />
                    </div>
                  )}
                </div>
              </div>
              {/* Le code école n'est PAS un moyen de paiement : c'est un tarif.
                  Il s'applique au moyen choisi ci-dessus, en remplaçant le prix
                  public par celui négocié avec l'établissement.

                  Masqué pour un établissement : il est chez lui, son contrat est
                  retrouvé par son organisation. Lui demander son propre code
                  n'aurait aucun sens. */}
              {estEtablissement ? (
                <Alert
                  type="info"
                  showIcon
                  icon={<BankOutlined />}
                  style={{ marginTop: 12 }}
                  message={t('payment.ecole.schoolModeTitle', 'Tarif de votre établissement')}
                  description={t(
                    'payment.ecole.schoolModeHint',
                    "Les comptes sélectionnés sont facturés au tarif négocié avec votre établissement. Le montant total vous est confirmé avant règlement."
                  )}
                />
              ) : (
              <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 12 }}>
                <Input
                  size="large"
                  value={codeEcole}
                  onChange={(e) => setCodeEcole(e.target.value)}
                  prefix={<BankOutlined style={{ opacity: 0.55 }} />}
                  placeholder={t('payment.ecole.placeholder', "Code de l'école (facultatif)")}
                  allowClear
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(
                    'payment.ecole.tariffHint',
                    "Si l'établissement de votre enfant est partenaire, saisissez son code : le tarif négocié avec l'école remplace le tarif public. Le montant exact vous est confirmé avant le paiement."
                  )}
                </Typography.Text>

                {/* Retour immédiat sur le code : le parent doit voir le tarif
                    qui lui sera appliqué avant de payer, pas le découvrir sur
                    son relevé. Trois issues, trois messages distincts. */}
                {codeEcole.trim() !== '' && infoCodeEcole?.valide && !infoCodeEcole.ecole_paie && (
                  <Alert
                    type="success"
                    showIcon
                    message={t(
                      'payment.ecole.tariffApplied',
                      'Tarif de votre école appliqué : {{prix}} {{devise}} par enfant et par mois.',
                      { prix: infoCodeEcole.prix ?? 0, devise: infoCodeEcole.devise ?? '' }
                    )}
                  />
                )}
                {codeEcole.trim() !== '' && infoCodeEcole?.valide && infoCodeEcole.ecole_paie && (
                  <Alert
                    type="info"
                    showIcon
                    message={t('payment.ecole.schoolPaysTitle', 'Abonnement géré par votre établissement')}
                    description={t(
                      'payment.ecole.schoolPaysHint',
                      "Cet établissement règle lui-même l'abonnement de ses élèves. Rapprochez-vous de l'école : vous n'avez rien à payer ici."
                    )}
                  />
                )}
                {codeEcole.trim() !== '' && infoCodeEcole && !infoCodeEcole.valide && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t(
                      'payment.ecole.codeUnknown',
                      "Ce code n'est pas reconnu. Le tarif public s'applique."
                    )}
                  />
                )}

                {/* Code animateur : distinct du code de l'école. Il attribue
                    les enfants payés à l'animateur, à l'encaissement. */}
                <div style={{ marginTop: 8 }}>
                  <CodeApporteurField value={codeApporteur} onChange={setCodeApporteur} />
                </div>
                {deuxCodes && (
                  <Alert
                    type="error"
                    showIcon
                    message={t(
                      'parrainage.paiement.deuxCodes',
                      "Saisissez soit le code de l'école, soit le code animateur, pas les deux."
                    )}
                  />
                )}
              </Space>
              )}
              {selectedVendor === 'lemonsqueezy' && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={t('payment.lemonsqueezy.subscriptionTitle', 'Abonnement reconductible')}
                  description={t(
                    'payment.lemonsqueezy.subscriptionHint',
                    "Le paiement par carte est un abonnement : vous êtes prélevé automatiquement à chaque échéance jusqu'à résiliation. La durée choisie ci-dessus ne s'applique qu'au paiement CashPlus. Le montant exact vous est confirmé sur la page de paiement."
                  )}
                />
              )}
            </Space>
          </div>
        </Space>
      </Modal>
    </AppLayout>
  );
};

export default Paiement;
